import { ownedBy, type PendingAttempt } from './outbox';
import { SM_KIND, type SmAttemptValue } from './schemas';
import type { Attempt, DaySession } from './sessions';

const attemptKey = (kind: string, completedAt: string | undefined) => `${kind}|${completedAt}`;

/**
 * The sessions with the person's attempts that are not in Core's history yet, as done attempts,
 * one row each: queued ones, and ones Core has recorded (`sentAt`). An exercise finished offline
 * counts as done right away, and its material is not offered again. An attempt Core's history
 * already has (same exercise, same `completedAt`) is left out, so nothing counts twice. The rows carry no `writtenAs`, so
 * nothing is ever written into them (`sessionOn`).
 */
export function withQueued(
  sessions: readonly DaySession[],
  queued: readonly PendingAttempt[],
): readonly DaySession[] {
  const saved = savedKeys(sessions);
  const rows = queued
    .filter((item) => !saved.has(attemptKey(item.input.kind, item.input.completedAt)))
    .map(
      (item): DaySession => ({
        uuid: `queued:${item.id}`,
        lineageUuid: `queued:${item.id}`,
        date: item.date,
        timeZone: item.timeZone,
        attempts: [queuedAttempt(item)],
      }),
    );
  return rows.length === 0 ? sessions : [...sessions, ...rows];
}

const savedKeys = (sessions: readonly DaySession[]) =>
  new Set(
    sessions.flatMap((session) =>
      session.attempts.map((attempt) => attemptKey(attempt.kind, attempt.value.completedAt)),
    ),
  );

/** How long an attempt Core recorded is kept to count locally. */
// ponytail: 3 days, so an attempt Core's history never echoes cannot linger.
const SENT_KEEP_MS = 3 * 24 * 60 * 60_000;

/**
 * The outbox without the person's recorded attempts that Core's history now shows, or that are
 * older than `SENT_KEEP_MS`; undefined when nothing goes. Unsent attempts and other people's
 * are never touched.
 */
export function pruneSent(
  items: readonly PendingAttempt[],
  person: string | undefined,
  sessions: readonly DaySession[],
  now: number,
): PendingAttempt[] | undefined {
  const mine = new Set(ownedBy(items, person));
  const saved = savedKeys(sessions);
  const kept = items.filter(
    (item) =>
      !mine.has(item) ||
      item.sentAt === undefined ||
      (now - item.sentAt <= SENT_KEEP_MS &&
        !saved.has(attemptKey(item.input.kind, item.input.completedAt))),
  );
  return kept.length === items.length ? undefined : kept;
}

function queuedAttempt({ id, input }: PendingAttempt): Attempt {
  const value: SmAttemptValue = {
    kind: SM_KIND[input.kind],
    state: 'done',
    source: input.source,
    ...(input.sourceTitle ? { sourceTitle: input.sourceTitle } : {}),
    startedAt: input.startedAt,
    completedAt: input.completedAt,
    activeSeconds: input.activeSeconds,
    markedBy: 'self',
  };
  return {
    uuid: `queued:${id}`,
    lineageUuid: `queued:${id}`,
    kind: input.kind,
    value,
    includes: [
      ...(input.includesLineageUuid ? [input.includesLineageUuid] : []),
      ...(input.alsoIncludesLineageUuids ?? []),
    ],
    answers: input.answers ?? [],
  };
}
